import { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '@/hooks'
import { useProgramRequirements } from '@/app/hooks/useProgramRequirements'
import useApiDispatch from '@/hooks/useApiDispatch/useApiDispatch'
import {
  selectInProgressEnrollment,
  setUtilityAccountNumber,
} from '@/reducers/inProgressUtilityProgramEnrollments'
import { useMultiProgramRouteContext } from '@/app/pages/multi-program-enrollment'
import { utilityAccountsCollection } from '@/reducers/utilityAccounts'
import { useFormik } from 'formik'
import { UseFormRegister } from 'react-hook-form'
import { AccountFieldKey } from '@/types/utilityProgram'
import { programsNewCollection } from '@/reducers/programsNew'
import {
  buildAccountFieldsValidationSchema,
  buildInitialValues,
  buildPatchPayload,
  resolveAccountFieldsConfig,
  useUtilityBillUpload,
} from '@/app/features/utility-account-fields'

type AccountFieldValues = Record<AccountFieldKey, string>

export function useViewModel() {
  const { utilityProgram } = useMultiProgramRouteContext()
  const { routeToNextStep } = useProgramRequirements(utilityProgram)
  const dispatch = useAppDispatch()
  const home = useAppSelector((state) => state.homes?.selectedHome)
  const { apiDispatch } = useApiDispatch()

  const inProgressEnrollment = useAppSelector(
    selectInProgressEnrollment(utilityProgram.id),
  )

  const { data: utilityAccountData } = utilityAccountsCollection.useFetch(
    '/utilities/utility_account',
  )
  const savedAccount = utilityAccountData?.[0]

  const config = useMemo(
    () => resolveAccountFieldsConfig(utilityProgram),
    [utilityProgram],
  )

  const utilityBillExampleSrc =
    utilityProgram.view_config_json?.accountNumberEntry?.utilityBillExampleImage
  const utilityWebsiteLink = useAppSelector(
    (state) => state.utilities.selectedUtility?.website,
  )

  const validationSchema = useMemo(
    () => buildAccountFieldsValidationSchema(config),
    [config],
  )

  const upload = useUtilityBillUpload({
    config,
    utilityProgramId: utilityProgram.id,
  })

  const formik = useFormik<AccountFieldValues>({
    enableReinitialize: true,
    // Seed empty fields from a previously-uploaded bill's extraction.
    initialValues: buildInitialValues(
      config,
      savedAccount,
      inProgressEnrollment?.utilityAccountNumber,
      upload.persistedExtraction,
    ),
    validationSchema,
    // Runs only when validation passes (handleNext triggers it via submitForm()).
    // On failure formik surfaces the errors and does not proceed.
    onSubmit: (values, helpers) => {
      const payload = buildPatchPayload(config, values)
      const response = apiDispatch(
        utilityAccountsCollection.actions.update(
          `/utilities/utility_account/${home?.id}`,
          payload,
          { id: home?.id },
        ),
      )

      response.then((result) => {
        if (result.ok) {
          dispatch(programsNewCollection.actions.invalidate())
          if (config.some((f) => f.field === 'account_number')) {
            dispatch(
              setUtilityAccountNumber({
                utilityProgramId: utilityProgram.id,
                utilityAccountNumber: values.account_number ?? '',
              }),
            )
          }
          routeToNextStep()
        } else {
          helpers.setFieldError(
            config[0]?.field ?? 'account_number',
            'There was an error updating your account. Please contact support@optiwatt.com if the problem persists.',
          )
        }
      })
    },
  })

  // Mirror bill-extracted values into the form (and the in-progress enrollment
  // for the account number, which the review step displays).
  useEffect(() => {
    const entries = Object.entries(upload.extractedValues) as [
      AccountFieldKey,
      string,
    ][]
    if (entries.length === 0) {
      return
    }
    entries.forEach(([field, value]) =>
      formik.setFieldValue(field, value, true),
    )
    if (upload.extractedValues.account_number) {
      dispatch(
        setUtilityAccountNumber({
          utilityProgramId: utilityProgram.id,
          utilityAccountNumber: upload.extractedValues.account_number,
        }),
      )
    }
  }, [upload.extractedValues])

  const register = ((name: string) => {
    return {
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
        formik.handleChange(event)
      },
      onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
        formik.handleBlur(event)
      },
      name,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ref: (_element: HTMLInputElement | null) => {
        // No-op ref for formik compatibility
      },
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }) as unknown as UseFormRegister<any>

  // Always clickable: validate and surface any errors; navigation happens in the
  // formik onSubmit above only when validation passes.
  const handleNext = () => {
    formik.submitForm()
  }

  return {
    config,
    upload,
    formik,
    register,
    utilityBillExampleSrc,
    utilityWebsiteLink,
    handleNext,
  }
}
